import { useEffect, useRef, type FC, type ReactNode } from 'react';
import { useFragment, useRefetchableFragment, graphql } from 'react-relay';
import classnames from 'classnames';
import { VideoTile } from 'dibs-elements/exports/VideoTile.ts';
import { VideoPlayer } from 'dibs-video/exports/VideoPlayer.ts';
import { getBuyerId } from 'dibs-cookie-jar';
import { useClientState } from 'dibs-react-hooks/exports/useClientState.ts';

import { getBackgroundColor, type ImageBackgroundColor } from '../constants.ts';

import { type ProductVideo_viewer$key } from './__generated__/ProductVideo_viewer.graphql.ts';
import { type ProductVideoRefetch_viewer$key } from './__generated__/ProductVideoRefetch_viewer.graphql.ts';
import { type ProductVideo_item$key } from './__generated__/ProductVideo_item.graphql.ts';
import { type ProductVideoRefetchQuery } from './__generated__/ProductVideoRefetchQuery.graphql.ts';

import dibsCss from 'dibs-css';
import styles from '../styles/styles.scss';

const viewerFragment = graphql`
    fragment ProductVideo_viewer on Viewer {
        vimeoItemsVideoHLSPlayerFF: featureFlag(feature: "vimeoItemsVideoHLSPlayer")
        muxMonitoringFF: featureFlag(feature: "muxMonitoring")
        ...ProductVideoRefetch_viewer
    }
`;

const refetchableViewerFragment = graphql`
    fragment ProductVideoRefetch_viewer on Viewer
    @argumentDefinitions(itemId: { type: "String" })
    @refetchable(queryName: "ProductVideoRefetchQuery") {
        video: itemVideoV2(itemId: $itemId) {
            url
            vimeoUrl
            videoProvider
        }
    }
`;

const itemFragment = graphql`
    fragment ProductVideo_item on Item {
        serviceId
    }
`;

type Props = {
    viewer: ProductVideo_viewer$key | null | undefined;
    item: ProductVideo_item$key | null | undefined;
    children: ReactNode;
    isHovering: boolean;
    imageBackgroundColor?: ImageBackgroundColor;
    onPlay: () => void;
};

const ProductVideo: FC<Props> = ({
    viewer: viewerRef,
    item: itemRef,
    children,
    isHovering,
    imageBackgroundColor,
    onPlay,
}) => {
    const viewer = useFragment(viewerFragment, viewerRef);
    const [data, refetch] = useRefetchableFragment<
        ProductVideoRefetchQuery,
        ProductVideoRefetch_viewer$key
    >(refetchableViewerFragment, viewer);
    const item = useFragment(itemFragment, itemRef);
    const videoPlayerRef = useRef<HTMLVideoElement>(null);
    const video = data?.video;
    const vimeoItemsVideoHLSPlayerFF = !!viewer?.vimeoItemsVideoHLSPlayerFF;
    const muxMonitoringFF = !!viewer?.muxMonitoringFF;
    const { serviceId: itemId } = item || {};
    const isClient = useClientState();
    const viewerUserId = isClient ? getBuyerId(document.cookie) : '';

    useEffect(() => {
        if (isHovering && !video && itemId) {
            refetch({ itemId });
        }
    }, [isHovering, itemId, refetch, video]);

    const sourceUrl = video?.url || '';
    const videoProvider = video?.videoProvider || '';
    const backgroundColor = getBackgroundColor(imageBackgroundColor);

    return (
        <div
            className={classnames(
                dibsCss.wFull,
                dibsCss.relative,
                styles.hideOverlay,
                backgroundColor
            )}
            data-tn="buyer-product-video"
        >
            {!!video?.vimeoUrl && videoProvider === 'VIMEO_ID' && !vimeoItemsVideoHLSPlayerFF ? (
                <VideoTile
                    animate={isHovering}
                    sourceUrl={video?.vimeoUrl}
                    objectFit="contain"
                    objectPosition="center"
                    noBackground
                    onPlay={onPlay}
                />
            ) : null}
            {sourceUrl &&
            (videoProvider === 'MUX_ID' ||
                (videoProvider === 'VIMEO_ID' && vimeoItemsVideoHLSPlayerFF)) ? (
                <div
                    className={classnames(
                        dibsCss.absolute,
                        dibsCss.hAuto,
                        dibsCss.top50,
                        dibsCss.left50,
                        styles.videoContainer
                    )}
                >
                    <VideoPlayer ref={videoPlayerRef}>
                        <VideoPlayer.Media
                            src={sourceUrl}
                            playback={{ autoPlay: isHovering, loop: true }}
                            callbacks={{ onPlay }}
                            monitor={muxMonitoringFF}
                            monitorMetadata={{ viewerUserId }}
                        />
                    </VideoPlayer>
                </div>
            ) : null}
            {children}
        </div>
    );
};
export default ProductVideo;
